import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../../hooks/useOrg';
import { atpRecordService } from '../../services/atpRecordService';
import { labRequestService } from '../../services/labRequestService';
import { useNotifications } from '../NotificationSystem';
import ATPRecordForm from './ATPRecordForm';
import LabRequestForm from './LabRequestForm';
import SamplingRecordDetailModal from './SamplingRecordDetailModal';
import './SamplingDashboard.css';

const INTERPRETATION_COLORS = {
  conform: { bg: '#f0fdf4', color: '#166534', label: 'Conform' },
  neconform: { bg: '#fef2f2', color: '#991b1b', label: 'Neconform' },
  echivoc: { bg: '#fffbeb', color: '#92400e', label: 'Echivoc' },
  in_lucru: { bg: '#eff6ff', color: '#1e40af', label: 'În lucru' },
  na: { bg: '#f9fafb', color: '#6b7280', label: 'N/A' },
  result_received: { bg: '#f0fdf4', color: '#166534', label: 'Rezultat primit' },
  pending: { bg: '#eff6ff', color: '#1e40af', label: 'În așteptare' }
};

/**
 * @param {object} props
 * @param {boolean} [props.canCreate] — false pentru department_head (doar vizualizare)
 */
const SamplingDashboard = ({ userData, canCreate = true }) => {
  const { activeOrgId } = useOrg();
  const { notify } = useNotifications();

  const [listTab, setListTab] = useState('atp');
  const [panel, setPanel] = useState('list');
  const [atpRecords, setAtpRecords] = useState([]);
  const [labRequests, setLabRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabRequest, setSelectedLabRequest] = useState(null);
  const [detailAtp, setDetailAtp] = useState(null);
  const [detailLab, setDetailLab] = useState(null);

  const load = useCallback(async () => {
    if (!activeOrgId) return null;
    setLoading(true);
    try {
      const [atp, lab] = await Promise.all([
        atpRecordService.getATPRecords(activeOrgId, { limit: 50 }),
        labRequestService.getLabRequests(activeOrgId, { limit: 50 })
      ]);
      setAtpRecords(atp);
      setLabRequests(lab);
      return { atp, lab };
    } catch (err) {
      console.error('[SamplingDashboard] load failed:', err?.code || err?.message, err);
      notify.error('Eroare la încărcare date');
      return null;
    } finally {
      setLoading(false);
    }
    // notify nu e în deps — referința se schimbă des și redeclanșa încărcarea în buclă + toast-uri repetate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (listTab === 'atp') setDetailLab(null);
    else setDetailAtp(null);
  }, [listTab]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const InterpretationBadge = ({ value }) => {
    const cfg = INTERPRETATION_COLORS[value] || INTERPRETATION_COLORS.na;
    return (
      <span style={{ background: cfg.bg, color: cfg.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
        {cfg.label}
      </span>
    );
  };

  if (panel === 'new-atp') {
    return (
      <div className="sampling-dashboard">
        <div className="sampling-form-container">
          <button type="button" className="btn-back" onClick={() => setPanel('list')}>← Înapoi</button>
          <ATPRecordForm
            onSaved={async (result) => {
              const data = await load();
              setPanel('list');
              setListTab('atp');
              if (result?.id && data?.atp) {
                const row = data.atp.find((r) => r.id === result.id);
                if (row) setDetailAtp(row);
              }
            }}
          />
        </div>
      </div>
    );
  }

  if (panel === 'new-lab') {
    return (
      <div className="sampling-dashboard">
        <div className="sampling-form-container">
          <button type="button" className="btn-back" onClick={() => setPanel('list')}>← Înapoi</button>
          <LabRequestForm
            mode="create"
            onSaved={async (result) => {
              const data = await load();
              setPanel('list');
              setListTab('lab');
              if (result?.id && data?.lab) {
                const row = data.lab.find((r) => r.id === result.id);
                if (row) setDetailLab(row);
              }
            }}
          />
        </div>
      </div>
    );
  }

  if (panel === 'result-lab' && selectedLabRequest) {
    return (
      <div className="sampling-dashboard">
        <div className="sampling-form-container">
          <button type="button" className="btn-back" onClick={() => { setPanel('list'); setSelectedLabRequest(null); }}>← Înapoi</button>
          <LabRequestForm
            mode="result"
            labRequest={selectedLabRequest}
            onSaved={async () => {
              const savedId = selectedLabRequest?.id;
              const data = await load();
              setPanel('list');
              setSelectedLabRequest(null);
              setListTab('lab');
              if (savedId && data?.lab) {
                const row = data.lab.find((r) => r.id === savedId);
                if (row) setDetailLab(row);
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sampling-dashboard">
      <div className="sampling-header">
        <h2>Probe & Testare</h2>
        {canCreate && (
          <div className="sampling-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setPanel('new-atp')}>+ Test ATP</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPanel('new-lab')}>+ Cerere Lab</button>
          </div>
        )}
      </div>

      <div className="sampling-tabs">
        <button type="button" className={`tab ${listTab === 'atp' ? 'active' : ''}`} onClick={() => setListTab('atp')}>Teste ATP</button>
        <button type="button" className={`tab ${listTab === 'lab' ? 'active' : ''}`} onClick={() => setListTab('lab')}>Cereri Laborator</button>
      </div>

      {listTab === 'atp' && (
        <div className="sampling-list">
          {loading && <div className="loading">Se încarcă...</div>}
          {!loading && atpRecords.length === 0 && <div className="empty-state">Nicio înregistrare ATP</div>}
          {!loading && atpRecords.map((r) => (
            <div
              key={r.id}
              className="sampling-row sampling-row--clickable"
              role="button"
              tabIndex={0}
              onClick={() => setDetailAtp(r)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDetailAtp(r);
                }
              }}
            >
              <div className="sampling-row-id">#{r.atp_id}</div>
              <div className="sampling-row-location">{r.room_name} — {r.surface_type_name}</div>
              <div className="sampling-row-value">{r.value !== null && r.value !== undefined ? `${r.value} RLU` : '—'}</div>
              <InterpretationBadge value={r.interpretation} />
              <div className="sampling-row-date">{formatDate(r.created_at)}</div>
              <div className="sampling-row-operator">{r.operator_name}</div>
              <span className="sampling-row-hint" aria-hidden="true">Vezi</span>
            </div>
          ))}
        </div>
      )}

      {listTab === 'lab' && (
        <div className="sampling-list">
          {loading && <div className="loading">Se încarcă...</div>}
          {!loading && labRequests.length === 0 && <div className="empty-state">Nicio cerere de laborator</div>}
          {!loading && labRequests.map((r) => (
            <div
              key={r.id}
              className="sampling-row sampling-row--clickable"
              role="button"
              tabIndex={0}
              onClick={() => setDetailLab(r)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDetailLab(r);
                }
              }}
            >
              <div className="sampling-row-id">#{r.request_id}</div>
              <div className="sampling-row-location">{r.room_name} — {r.sample_type}</div>
              <div className="sampling-row-method">{r.test_method}</div>
              <InterpretationBadge value={r.status === 'result_received' ? (r.result_interpretation || 'result_received') : r.status} />
              <div className="sampling-row-date">{formatDate(r.requested_at)}</div>
              {canCreate && r.status !== 'result_received' && (
                <button
                  type="button"
                  className="btn btn-secondary btn-xs btn-row-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedLabRequest(r);
                    setPanel('result-lab');
                  }}
                >
                  Înregistrează rezultat
                </button>
              )}
              <span className="sampling-row-hint" aria-hidden="true">Vezi</span>
            </div>
          ))}
        </div>
      )}

      <SamplingRecordDetailModal
        type="atp"
        record={detailAtp}
        isOpen={!!detailAtp}
        onClose={() => setDetailAtp(null)}
        formatDate={formatDate}
      />

      <SamplingRecordDetailModal
        type="lab"
        record={detailLab}
        isOpen={!!detailLab}
        onClose={() => setDetailLab(null)}
        formatDate={formatDate}
        canRecordLabResult={canCreate && detailLab && detailLab.status !== 'result_received'}
        onRecordLabResult={() => {
          if (!detailLab) return;
          setSelectedLabRequest(detailLab);
          setDetailLab(null);
          setPanel('result-lab');
        }}
      />
    </div>
  );
};

export default SamplingDashboard;
